import { makeAutoObservable, observable } from 'mobx';
import type { LogicNodeInstance } from './LogicNodeInstance';

export interface DebugTiming {
  label: string;
  scope: string;
  message?: string;
  milliseconds?: number;
  ticks?: number;
}

/** Instance path only: tile IDs never participate in label pairing. */
export function debugTimerScope(key = ''): string {
  if (key.startsWith('chip:')) {
    const colon = key.indexOf(':', 5);
    const length = Number(key.slice(5, colon));
    if (colon < 0 || !Number.isSafeInteger(length) || length < 0) return key;
    const end = colon + 1 + length;
    return key.slice(0, end) + debugTimerScope(key.slice(end));
  }
  try {
    const value = JSON.parse(key);
    if (value[0] === 'lift') return `lane:${value[1]}:${debugTimerScope(value[2])}`;
  } catch {
    /* Root nodes need no instance prefix. */
  }
  return '';
}

/** Bounded session diagnostics, deliberately excluded from circuit DTOs/checkpoints. */
export class DebugTimers {
  enabled = false;
  entries: DebugTiming[] = [];
  private tick = 0;
  private active = new Map<string, { at: number; tick: number }>();
  private pending: { start: boolean; label: string; scope: string }[] = [];

  constructor() {
    makeAutoObservable<this, 'tick' | 'active' | 'pending'>(this, {
      entries: observable.shallow,
      tick: false,
      active: false,
      pending: false,
    });
  }
  setEnabled(enabled: boolean) {
    if (enabled === this.enabled) return;
    this.enabled = enabled;
    this.clear();
  }
  clear() {
    this.entries = [];
    this.cancel();
  }
  cancel() {
    this.active.clear();
    this.pending.length = 0;
    this.tick = 0;
  }
  beginWave() {
    this.tick++;
  }
  observe(node: LogicNodeInstance, input: boolean) {
    if (!input || node.state.lastInput) return;
    this.pending.push({
      start: node.type === 'DEBUG_TIMER_START',
      label: node.state.label,
      scope: debugTimerScope(node.runtimeKey),
    });
  }
  finishWave() {
    if (!this.pending.length) return;
    // One physical timestamp per input snapshot. Starts precede ends regardless
    // of graph traversal order, so a same-wave pair measures exactly 0/0.
    const now = performance.now();
    this.pending.sort((a, b) => Number(b.start) - Number(a.start));
    for (const { start, label, scope } of this.pending) {
      const key = JSON.stringify([scope, label]);
      const previous = this.active.get(key);
      if (start) {
        if (previous)
          this.append({ label, scope, message: 'Already running; start ignored' });
        else if (this.active.size < 1000)
          this.active.set(key, { at: now, tick: this.tick });
        else this.append({ label, scope, message: 'Timer limit reached; start ignored' });
      } else if (!previous) {
        this.append({ label, scope, message: 'No running timer; end ignored' });
      } else {
        this.active.delete(key);
        this.append({
          label,
          scope,
          milliseconds: Math.max(0, now - previous.at),
          ticks: this.tick - previous.tick,
        });
      }
    }
    this.pending.length = 0;
  }
  private append(entry: DebugTiming) {
    this.entries.push(entry);
    if (this.entries.length > 100) this.entries.shift();
  }
  replaceEntries(entries: DebugTiming[]) {
    this.entries = entries.slice(-100);
  }
}
